<!-- FieldFixedExpensesPanel.vue - Panel para gestionar gastos fijos por agencia -->
<template>
  <div class="field-fixed-expenses-panel">
    <!-- Resumen de gastos fijos -->
    <v-card class="mb-4" variant="outlined" color="info">
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <div class="text-center">
              <div class="text-h5 font-weight-bold">{{ fixedExpenses.length }}</div>
              <div class="text-caption">Gastos Fijos Configurados</div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="text-center">
              <div class="text-h5 font-weight-bold text-warning">RD$ {{ formatNumber(totalEstimado) }}</div>
              <div class="text-caption">Total Mensual Estimado</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Alerta de gastos pendientes -->
    <v-alert
      v-if="pendingExpenses.length"
      type="warning"
      variant="tonal"
      class="mb-4"
      closable
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        <span>
          <strong>{{ pendingExpenses.length }}</strong> gastos fijos pendientes de registro este mes
        </span>
        <v-spacer></v-spacer>
        <v-btn
          size="small"
          color="warning"
          variant="tonal"
          @click="showPendingDialog = true"
        >
          Ver Pendientes
        </v-btn>
      </div>
    </v-alert>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" color="white" bg-color="info" slider-color="white" class="mb-4">
      <v-tab value="monthly">
        <v-icon start>mdi-calendar-month</v-icon>
        Seguimiento Mensual
      </v-tab>
      <v-tab value="list">
        <v-icon start>mdi-format-list-bulleted</v-icon>
        Gastos Fijos
      </v-tab>
      <v-tab value="new">
        <v-icon start>mdi-plus-circle</v-icon>
        Configurar Nuevo
      </v-tab>
      <v-tab value="history">
        <v-icon start>mdi-history</v-icon>
        Historial de Pagos
      </v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- Tab: Seguimiento Mensual -->
      <v-window-item value="monthly">
        <MonthlyFixedExpensesTracker 
          @expense-paid="onMonthlyExpensePaid"
          @expense-skipped="onMonthlyExpenseSkipped"
          @month-generated="onMonthGenerated"
        />
      </v-window-item>

      <!-- Tab: Lista de Gastos Fijos -->
      <v-window-item value="list">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon start>mdi-home-city</v-icon>
            Gastos Fijos por Agencia
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-refresh"
              variant="text"
              size="small"
              @click="loadFixedExpenses"
              :loading="loading"
            ></v-btn>
          </v-card-title>

          <v-card-text>
            <!-- Filtros -->
            <v-row class="mb-3">
              <v-col cols="6">
                <v-autocomplete
                  v-model="filters.agency_id"
                  :items="agencies"
                  item-title="name"
                  item-value="id"
                  label="Filtrar por Agencia"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadFixedExpenses"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="filters.categoria"
                  :items="categoryOptions"
                  item-title="text"
                  item-value="value"
                  label="Filtrar por Categoría"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadFixedExpenses"
                ></v-select>
              </v-col>
            </v-row>

            <!-- Lista de gastos fijos -->
            <v-list v-if="fixedExpenses.length" lines="two">
              <v-list-item
                v-for="expense in fixedExpenses"
                :key="expense.id"
                class="mb-2 rounded border"
              >
                <template v-slot:prepend>
                  <v-avatar :color="getCategoryColor(expense.categoria)" size="40">
                    <v-icon color="white">{{ getCategoryIcon(expense.categoria) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>
                  <strong>{{ expense.nombre }}</strong>
                  <v-chip size="x-small" class="ml-2" :color="getCategoryColor(expense.categoria)">
                    {{ getCategoryText(expense.categoria) }}
                  </v-chip>
                  <!-- Indicador de fuente de financiamiento -->
                  <v-chip 
                    size="x-small" 
                    class="ml-1" 
                    :color="expense.usa_fondo_grupo ? 'primary' : 'teal'"
                    variant="outlined"
                  >
                    <v-icon start size="10">{{ expense.usa_fondo_grupo ? 'mdi-wallet' : 'mdi-domain' }}</v-icon>
                    {{ expense.usa_fondo_grupo ? 'Fondo' : 'Empresa' }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <div>
                    <v-icon size="14">mdi-store</v-icon>
                    {{ expense.agency?.name || 'N/A' }} |
                    <v-icon size="14">mdi-currency-usd</v-icon>
                    RD$ {{ formatNumber(expense.monto_estimado) }}/mes
                  </div>
                  <div class="text-caption" v-if="expense.dia_vencimiento">
                    <v-icon size="12">mdi-calendar-alert</v-icon>
                    Vence: {{ formatDiaVencimiento(expense.dia_vencimiento, expense.dia_especifico) }}
                  </div>
                  <!-- Mostrar fecha fin de contrato para alquileres -->
                  <div class="text-caption" v-if="expense.categoria === 'alquiler' && expense.fecha_fin_contrato">
                    <v-icon size="12">mdi-calendar-end</v-icon>
                    Contrato hasta: {{ formatDate(expense.fecha_fin_contrato) }}
                  </div>
                </v-list-item-subtitle>

                <template v-slot:append>
                  <div class="d-flex flex-column align-end">
                    <!-- Alerta de contrato próximo a vencer -->
                    <v-chip
                      v-if="expense.alerta_contrato"
                      size="x-small"
                      :color="expense.alerta_contrato.tipo === 'urgente' ? 'error' : (expense.alerta_contrato.tipo === 'vencido' ? 'grey-darken-1' : 'warning')"
                      class="mb-1"
                    >
                      <v-icon start size="12">mdi-alert</v-icon>
                      {{ expense.alerta_contrato.mensaje }}
                    </v-chip>
                    <v-chip
                      v-if="isExpensePending(expense)"
                      size="x-small"
                      color="warning"
                      class="mb-1"
                    >
                      Pendiente
                    </v-chip>
                    <div class="d-flex">
                      <v-btn
                        v-if="isExpensePending(expense)"
                        icon="mdi-cash-register"
                        size="x-small"
                        color="success"
                        variant="tonal"
                        class="mr-1"
                        @click="openRegisterDialog(expense)"
                        title="Registrar Pago"
                      ></v-btn>
                      <v-btn
                        icon="mdi-pencil"
                        size="x-small"
                        variant="text"
                        class="mr-1"
                        @click="openEditDialog(expense)"
                      ></v-btn>
                      <v-btn
                        icon="mdi-delete"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="confirmDelete(expense)"
                      ></v-btn>
                    </div>
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <v-alert v-else type="info" variant="tonal">
              No hay gastos fijos configurados
            </v-alert>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- Tab: Nuevo Gasto Fijo -->
      <v-window-item value="new">
        <v-card variant="outlined">
          <v-card-title>
            <v-icon start>mdi-plus-circle</v-icon>
            Configurar Nuevo Gasto Fijo
          </v-card-title>
          
          <v-card-text>
            <v-form ref="newForm" v-model="newFormValid">
              <v-autocomplete
                v-model="newExpense.agency_id"
                :items="agencies"
                item-title="name"
                item-value="id"
                label="Agencia"
                prepend-icon="mdi-store"
                :rules="[v => !!v || 'Seleccione una agencia']"
                density="compact"
                class="mb-3"
              ></v-autocomplete>

              <v-text-field
                v-model="newExpense.nombre"
                label="Nombre del Gasto"
                prepend-icon="mdi-tag"
                placeholder="Ej: Alquiler Local, Electricidad, Agua"
                :rules="[v => !!v || 'El nombre es requerido']"
                density="compact"
                class="mb-3"
              ></v-text-field>

              <v-select
                v-model="newExpense.categoria"
                :items="categoryOptions"
                item-title="text"
                item-value="value"
                label="Categoría"
                prepend-icon="mdi-shape"
                :rules="[v => !!v || 'Seleccione una categoría']"
                density="compact"
                class="mb-3"
              ></v-select>

              <!-- Fecha fin de contrato (solo para alquiler) -->
              <v-text-field
                v-if="newExpense.categoria === 'alquiler'"
                v-model="newExpense.fecha_fin_contrato"
                type="date"
                label="Fecha Fin de Contrato"
                prepend-icon="mdi-calendar-end"
                :rules="[v => !!v || 'La fecha de fin de contrato es requerida para alquileres']"
                hint="Fecha en que vence el contrato de alquiler"
                persistent-hint
                density="compact"
                class="mb-3"
              ></v-text-field>

              <v-text-field
                v-model.number="newExpense.monto_estimado"
                type="number"
                label="Monto Estimado Mensual (RD$)"
                prepend-icon="mdi-currency-usd"
                :rules="[v => v > 0 || 'El monto debe ser mayor a 0']"
                min="0.01"
                step="0.01"
                density="compact"
                class="mb-3"
              ></v-text-field>

              <v-select
                v-model="newExpense.dia_vencimiento"
                :items="diaVencimientoOptions"
                item-title="text"
                item-value="value"
                label="Día de Vencimiento"
                prepend-icon="mdi-calendar"
                :rules="[v => !!v || 'El día de vencimiento es requerido']"
                density="compact"
                class="mb-3"
              ></v-select>

              <!-- Día específico si seleccionó esa opción -->
              <v-text-field
                v-if="newExpense.dia_vencimiento === 'dia_especifico'"
                v-model.number="newExpense.dia_especifico"
                type="number"
                label="Día Específico (1-28)"
                prepend-icon="mdi-calendar-today"
                :rules="[
                  v => !!v || 'Especifique el día',
                  v => (v >= 1 && v <= 28) || 'Día debe estar entre 1 y 28'
                ]"
                min="1"
                max="28"
                hint="Máximo 28 para evitar problemas con meses cortos"
                persistent-hint
                density="compact"
                class="mb-3"
              ></v-text-field>

              <v-textarea
                v-model="newExpense.notas"
                label="Notas (opcional)"
                prepend-icon="mdi-text"
                rows="2"
                density="compact"
                class="mb-3"
              ></v-textarea>

              <!-- Fuente de Financiamiento -->
              <v-divider class="mb-3"></v-divider>
              <div class="text-subtitle-2 mb-2">
                <v-icon start size="18">mdi-cash-multiple</v-icon>
                Fuente de Financiamiento
              </div>
              <v-switch
                v-model="newExpense.usa_fondo_grupo"
                color="primary"
                density="compact"
                hide-details
                class="mb-2"
              >
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <v-icon 
                      :color="newExpense.usa_fondo_grupo ? 'primary' : 'grey'" 
                      class="mr-2"
                    >
                      {{ newExpense.usa_fondo_grupo ? 'mdi-wallet' : 'mdi-domain' }}
                    </v-icon>
                    <div>
                      <div class="font-weight-medium">
                        {{ newExpense.usa_fondo_grupo ? 'Usa fondo del grupo' : 'Gasto corporativo (empresa)' }}
                      </div>
                      <div class="text-caption text-grey">
                        {{ newExpense.usa_fondo_grupo 
                          ? 'Se descontará del fondo asignado al supervisor' 
                          : 'La empresa cubre este gasto (no afecta el fondo del grupo)' 
                        }}
                      </div>
                    </div>
                  </div>
                </template>
              </v-switch>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" variant="text" @click="resetNewForm">Cancelar</v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!newFormValid"
              @click="createFixedExpense"
            >
              <v-icon start>mdi-check</v-icon>
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-window-item>

      <!-- Tab: Historial de Pagos -->
      <v-window-item value="history">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon start>mdi-history</v-icon>
            Historial de Pagos de Gastos Fijos
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-refresh"
              variant="text"
              size="small"
              @click="loadExpenseHistory"
              :loading="loadingHistory"
            ></v-btn>
          </v-card-title>
          
          <v-card-text>
            <!-- Filtros -->
            <v-row class="mb-3" dense>
              <v-col cols="12" md="3">
                <v-autocomplete
                  v-model="historyFilters.agency_id"
                  :items="agencies"
                  item-title="name"
                  item-value="id"
                  label="Agencia"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadExpenseHistory"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="historyFilters.categoria"
                  :items="categoryOptions"
                  item-title="text"
                  item-value="value"
                  label="Categoría"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="loadExpenseHistory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="historyFilters.date_range"
                  :items="dateRangeOptions"
                  item-title="label"
                  item-value="value"
                  label="Período"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="applyHistoryDateRange"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="historyFilters.fecha_desde"
                  type="date"
                  label="Desde"
                  density="compact"
                  hide-details
                  :disabled="historyFilters.date_range && historyFilters.date_range !== 'custom'"
                  @update:model-value="loadExpenseHistory"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="historyFilters.fecha_hasta"
                  type="date"
                  label="Hasta"
                  density="compact"
                  hide-details
                  :disabled="historyFilters.date_range && historyFilters.date_range !== 'custom'"
                  @update:model-value="loadExpenseHistory"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-list v-if="fixedExpenseHistory.length" lines="two">
              <v-list-item
                v-for="payment in fixedExpenseHistory"
                :key="payment.id"
                class="mb-2 rounded border cursor-pointer history-item"
                @click="openHistoryDetailDialog(payment)"
              >
                <template v-slot:prepend>
                  <v-avatar :color="getCategoryColor(payment.categoria)" size="40">
                    <v-icon color="white">{{ getCategoryIcon(payment.categoria) }}</v-icon>
                  </v-avatar>
                </template>

                <v-list-item-title>
                  <strong>{{ payment.concepto }}</strong>
                  <v-chip size="x-small" class="ml-2" :color="getCategoryColor(payment.categoria)">
                    {{ getCategoryText(payment.categoria) }}
                  </v-chip>
                </v-list-item-title>

                <v-list-item-subtitle>
                  <div>
                    <v-icon size="14">mdi-store</v-icon>
                    {{ payment.agency?.name || 'N/A' }} |
                    <v-icon size="14">mdi-currency-usd</v-icon>
                    <strong class="text-success">RD$ {{ formatNumber(payment.monto) }}</strong>
                  </div>
                  <div class="text-caption">
                    <v-icon size="12">mdi-calendar</v-icon>
                    {{ formatDate(payment.fecha_gasto) }}
                    <span v-if="payment.comprobante_numero" class="ml-2">
                      | <v-icon size="12">mdi-receipt</v-icon> {{ payment.comprobante_numero }}
                    </span>
                  </div>
                </v-list-item-subtitle>

                <template v-slot:append>
                  <div class="d-flex flex-column align-center">
                    <v-icon v-if="payment.documento_evidencia || payment.comprobante_url" size="small" color="success" title="Tiene documento de evidencia" class="mb-1">
                      mdi-paperclip
                    </v-icon>
                    <v-icon size="small" color="grey">mdi-chevron-right</v-icon>
                  </div>
                </template>
              </v-list-item>
            </v-list>

            <v-alert v-else type="info" variant="tonal">
              No hay pagos registrados
              <span v-if="historyFilters.date_range">en el período seleccionado</span>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Dialog: Registrar Pago -->
    <v-dialog v-model="registerDialog.show" max-width="550">
      <v-card>
        <v-card-title>
          <v-icon start color="success">mdi-cash-register</v-icon>
          Registrar Pago
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" class="mb-4">
            <strong>{{ registerDialog.expense?.nombre }}</strong><br>
            Agencia: {{ registerDialog.expense?.agency?.name }}<br>
            Monto estimado: RD$ {{ formatNumber(registerDialog.expense?.monto_estimado) }}
          </v-alert>

          <!-- Indicador de fuente de financiamiento -->
          <v-alert 
            :type="registerDialog.expense?.usa_fondo_grupo ? 'warning' : 'success'" 
            variant="tonal" 
            density="compact"
            class="mb-4"
          >
            <template #prepend>
              <v-icon>{{ registerDialog.expense?.usa_fondo_grupo ? 'mdi-wallet' : 'mdi-domain' }}</v-icon>
            </template>
            <span v-if="registerDialog.expense?.usa_fondo_grupo">
              <strong>Fondo del Grupo:</strong> Este pago se descontará del fondo disponible del grupo.
            </span>
            <span v-else>
              <strong>Gasto Corporativo:</strong> Este pago es cubierto por la empresa (no afecta el fondo del grupo).
            </span>
          </v-alert>

          <v-text-field
            v-model.number="registerDialog.monto_real"
            type="number"
            label="Monto Real Pagado (RD$)"
            prepend-icon="mdi-currency-usd"
            hint="Dejar vacío para usar el monto estimado"
            persistent-hint
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model="registerDialog.comprobante_numero"
            label="Número de Comprobante/Factura"
            prepend-icon="mdi-file-document"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <!-- Documento de Evidencia -->
          <v-file-input
            v-model="registerDialog.documento"
            label="Documento de Evidencia (opcional)"
            prepend-icon="mdi-paperclip"
            accept=".pdf,.jpg,.jpeg,.png"
            :hint="registerDialog.documentoError ? '' : `PDF o imagen del recibo/factura (máx. ${MAX_FILE_SIZE_MB}MB)`"
            persistent-hint
            density="compact"
            class="mb-3"
            :error="!!registerDialog.documentoError"
            :error-messages="registerDialog.documentoError"
            @update:model-value="handleDocumentoChange"
          >
            <template v-slot:selection="{ fileNames }">
              <v-chip 
                size="small" 
                :color="registerDialog.documentoError ? 'error' : 'primary'"
              >
                <v-icon start size="14">{{ registerDialog.documentoError ? 'mdi-alert-circle' : 'mdi-file-check' }}</v-icon>
                {{ fileNames[0] }}
                <span v-if="registerDialog.documentoSize" class="ml-1 text-caption">
                  ({{ registerDialog.documentoSize }})
                </span>
              </v-chip>
            </template>
          </v-file-input>

          <!-- Alerta de archivo muy grande -->
          <v-alert
            v-if="registerDialog.documentoError"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
            closable
            @click:close="registerDialog.documentoError = null"
          >
            <template v-slot:prepend>
              <v-icon>mdi-file-alert</v-icon>
            </template>
            {{ registerDialog.documentoError }}
            <div class="text-caption mt-1">
              💡 Tip: Reduce el tamaño de la imagen o usa un PDF comprimido.
            </div>
          </v-alert>

          <!-- Preview del documento si es imagen -->
          <v-img
            v-if="registerDialog.documentoPreview && isImageFile(registerDialog.documento) && !registerDialog.documentoError"
            :src="registerDialog.documentoPreview"
            max-height="150"
            class="mb-3 rounded border"
            contain
          ></v-img>

          <v-textarea
            v-model="registerDialog.observaciones"
            label="Observaciones"
            prepend-icon="mdi-text"
            rows="2"
            density="compact"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="registerDialog.show = false">Cancelar</v-btn>
          <v-btn 
            color="success" 
            :loading="registering" 
            :disabled="!!registerDialog.documentoError"
            @click="registerPayment"
          >
            <v-icon start>mdi-check</v-icon>
            Registrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Editar Gasto Fijo -->
    <v-dialog v-model="editDialog.show" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon start>mdi-pencil</v-icon>
          Editar Gasto Fijo
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editDialog.data.nombre"
            label="Nombre"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-select
            v-model="editDialog.data.categoria"
            :items="categoryOptions"
            item-title="text"
            item-value="value"
            label="Categoría"
            density="compact"
            class="mb-3"
          ></v-select>

          <!-- Fecha fin de contrato (solo para alquiler) -->
          <v-text-field
            v-if="editDialog.data.categoria === 'alquiler'"
            v-model="editDialog.data.fecha_fin_contrato"
            type="date"
            label="Fecha Fin de Contrato"
            prepend-icon="mdi-calendar-end"
            hint="Fecha en que vence el contrato de alquiler"
            persistent-hint
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-text-field
            v-model.number="editDialog.data.monto_estimado"
            type="number"
            label="Monto Estimado (RD$)"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-select
            v-model="editDialog.data.dia_vencimiento"
            :items="diaVencimientoOptions"
            item-title="text"
            item-value="value"
            label="Día de Vencimiento"
            density="compact"
            class="mb-3"
          ></v-select>

          <!-- Día específico si seleccionó esa opción -->
          <v-text-field
            v-if="editDialog.data.dia_vencimiento === 'dia_especifico'"
            v-model.number="editDialog.data.dia_especifico"
            type="number"
            label="Día Específico (1-28)"
            min="1"
            max="28"
            density="compact"
            class="mb-3"
          ></v-text-field>

          <!-- Fuente de financiamiento -->
          <v-switch
            v-model="editDialog.data.usa_fondo_grupo"
            color="primary"
            density="compact"
            hide-details
            class="mb-3"
          >
            <template #label>
              <span class="text-body-2">
                <v-icon size="16" class="mr-1">{{ editDialog.data.usa_fondo_grupo ? 'mdi-wallet' : 'mdi-domain' }}</v-icon>
                {{ editDialog.data.usa_fondo_grupo ? 'Usa Fondo del Grupo' : 'Gasto Corporativo (Empresa)' }}
              </span>
            </template>
          </v-switch>

          <v-textarea
            v-model="editDialog.data.notas"
            label="Notas"
            rows="2"
            density="compact"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="editDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="updateFixedExpense">
            <v-icon start>mdi-check</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Pendientes del Mes -->
    <v-dialog v-model="showPendingDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon start color="warning">mdi-alert-circle</v-icon>
          Gastos Fijos Pendientes de Registro
        </v-card-title>
        <v-card-text>
          <v-list lines="two">
            <v-list-item
              v-for="expense in pendingExpenses"
              :key="expense.id"
              class="mb-2 rounded border"
            >
              <v-list-item-title>{{ expense.nombre }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ expense.agency?.name }} | RD$ {{ formatNumber(expense.monto_estimado) }}
              </v-list-item-subtitle>
              <template v-slot:append>
                <v-btn
                  color="success"
                  size="small"
                  @click="openRegisterDialog(expense); showPendingDialog = false"
                >
                  Registrar
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showPendingDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Dialog: Detalle del Gasto Histórico -->
    <v-dialog v-model="historyDetailDialog.show" max-width="650">
      <v-card v-if="historyDetailDialog.expense" class="expense-detail-card">
        <!-- Header con gradiente -->
        <div class="expense-detail-header pa-4" :style="{ background: `linear-gradient(135deg, ${getCategoryColor(historyDetailDialog.expense.categoria)} 0%, ${getCategoryColorDark(historyDetailDialog.expense.categoria)} 100%)` }">
          <div class="d-flex align-center">
            <v-avatar size="56" color="white" class="elevation-3">
              <v-icon size="28" :color="getCategoryColor(historyDetailDialog.expense.categoria)">
                {{ getCategoryIcon(historyDetailDialog.expense.categoria) }}
              </v-icon>
            </v-avatar>
            <div class="ml-4 text-white">
              <div class="text-h6 font-weight-bold">{{ historyDetailDialog.expense.concepto }}</div>
              <div class="text-caption opacity-80">
                <v-chip size="x-small" color="white" variant="flat" class="mr-2">
                  {{ getCategoryText(historyDetailDialog.expense.categoria) }}
                </v-chip>
                Gasto Fijo
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" color="white" @click="historyDetailDialog.show = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="pa-0">
          <!-- Monto destacado -->
          <div class="text-center py-5 bg-grey-lighten-4">
            <div class="text-caption text-grey-darken-1 mb-1">MONTO PAGADO</div>
            <div class="text-h4 font-weight-bold text-success">
              RD$ {{ formatNumber(historyDetailDialog.expense.monto) }}
            </div>
          </div>

          <!-- Información en grid -->
          <v-container class="py-4">
            <v-row dense>
              <!-- Agencia -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-blue-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="blue" class="mr-2">mdi-store</v-icon>
                    <span class="text-caption text-grey-darken-1">Agencia</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ historyDetailDialog.expense.agency?.name || 'N/A' }}
                  </div>
                </div>
              </v-col>

              <!-- Fecha -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-orange-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="orange" class="mr-2">mdi-calendar</v-icon>
                    <span class="text-caption text-grey-darken-1">Fecha del Gasto</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDateLong(historyDetailDialog.expense.fecha_gasto) }}
                  </div>
                </div>
              </v-col>

              <!-- Fuente de Financiamiento -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg" :class="historyDetailDialog.expense.usa_fondo_grupo ? 'bg-amber-lighten-5' : 'bg-teal-lighten-5'">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" :color="historyDetailDialog.expense.usa_fondo_grupo ? 'amber-darken-2' : 'teal'" class="mr-2">
                      {{ historyDetailDialog.expense.usa_fondo_grupo ? 'mdi-wallet' : 'mdi-domain' }}
                    </v-icon>
                    <span class="text-caption text-grey-darken-1">Fuente</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ historyDetailDialog.expense.usa_fondo_grupo ? 'Fondo del Grupo' : 'Gasto Corporativo' }}
                  </div>
                </div>
              </v-col>

              <!-- Registrado por -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-purple-lighten-5">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="purple" class="mr-2">mdi-account</v-icon>
                    <span class="text-caption text-grey-darken-1">Registrado por</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ historyDetailDialog.expense.supervisor?.name || 'Sistema' }}
                  </div>
                </div>
              </v-col>

              <!-- Número de comprobante -->
              <v-col cols="12" sm="6" v-if="historyDetailDialog.expense.comprobante_numero">
                <div class="info-card pa-3 rounded-lg bg-grey-lighten-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="grey-darken-1" class="mr-2">mdi-receipt</v-icon>
                    <span class="text-caption text-grey-darken-1">Nº Comprobante</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ historyDetailDialog.expense.comprobante_numero }}
                  </div>
                </div>
              </v-col>

              <!-- Fecha de registro -->
              <v-col cols="12" sm="6">
                <div class="info-card pa-3 rounded-lg bg-grey-lighten-4">
                  <div class="d-flex align-center mb-1">
                    <v-icon size="18" color="grey-darken-1" class="mr-2">mdi-clock-outline</v-icon>
                    <span class="text-caption text-grey-darken-1">Registrado</span>
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatDateTime(historyDetailDialog.expense.createdAt) }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Observaciones -->
            <div v-if="historyDetailDialog.expense.observaciones" class="mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="18" color="grey-darken-1" class="mr-2">mdi-text</v-icon>
                <span class="text-subtitle-2">Observaciones</span>
              </div>
              <div class="pa-3 rounded-lg bg-grey-lighten-4 text-body-2">
                {{ historyDetailDialog.expense.observaciones }}
              </div>
            </div>

            <!-- Documento de evidencia -->
            <div v-if="historyDetailDialog.expense.tiene_documento" class="mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="18" color="success" class="mr-2">mdi-paperclip</v-icon>
                <span class="text-subtitle-2">Documento de Evidencia</span>
                <v-progress-circular
                  v-if="historyDetailDialog.expense.loading"
                  indeterminate
                  size="16"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </div>
              
              <!-- Mostrar imagen si está disponible (soporte para BLOB o URL) -->
              <template v-if="historyDetailDialog.expense.documento_evidencia || historyDetailDialog.expense.comprobante_url">
                <!-- Caso 1: URL de archivo (nuevo sistema multer) -->
                <template v-if="historyDetailDialog.expense.comprobante_url">
                  <v-img
                    v-if="!historyDetailDialog.expense.comprobante_url.toLowerCase().endsWith('.pdf')"
                    :src="getFullUrl(historyDetailDialog.expense.comprobante_url)"
                    max-height="350"
                    class="rounded-lg border document-preview"
                  >
                    <template v-slot:error>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-icon size="48" color="grey">mdi-image-broken</v-icon>
                      </div>
                    </template>
                  </v-img>
                  <div v-else class="text-center pa-4">
                    <v-icon size="64" color="error">mdi-file-pdf-box</v-icon>
                    <p class="mt-2 mb-2">Documento PDF</p>
                    <v-btn
                      color="primary"
                      :href="getFullUrl(historyDetailDialog.expense.comprobante_url)"
                      target="_blank"
                    >
                      <v-icon start>mdi-open-in-new</v-icon>
                      Abrir PDF
                    </v-btn>
                  </div>
                </template>
                <!-- Caso 2: BLOB base64 (sistema legacy) -->
                <template v-else-if="historyDetailDialog.expense.documento_evidencia">
                  <v-img
                    v-if="historyDetailDialog.expense.documento_evidencia_tipo?.startsWith('image/')"
                    :src="getDocumentDataUri(historyDetailDialog.expense)"
                    max-height="350"
                    class="rounded-lg border document-preview"
                  ></v-img>
                  <v-btn
                    v-else
                    color="primary"
                    variant="tonal"
                    :href="getDocumentDataUri(historyDetailDialog.expense)"
                    :download="historyDetailDialog.expense.documento_evidencia_nombre || 'documento'"
                    block
                  >
                    <v-icon start>mdi-download</v-icon>
                    Descargar {{ historyDetailDialog.expense.documento_evidencia_nombre || 'documento' }}
                  </v-btn>
                </template>
              </template>
              
              <!-- Placeholder mientras carga -->
              <v-skeleton-loader
                v-else-if="historyDetailDialog.expense.loading"
                type="image"
                height="200"
              ></v-skeleton-loader>
              
              <!-- Indicador de que hay documento pero aún no cargó -->
              <v-alert
                v-else
                type="info"
                variant="tonal"
                density="compact"
              >
                <v-icon start>mdi-file-document</v-icon>
                Documento adjunto: {{ historyDetailDialog.expense.documento_evidencia_nombre || 'archivo' }}
              </v-alert>
            </div>
          </v-container>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-chip size="small" color="success" variant="tonal">
            <v-icon start size="14">mdi-check-circle</v-icon>
            Registrado
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="historyDetailDialog.show = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ApiClient from '@/services/api/api-client'
import { getApiOriginUrl } from '@/config/runtimeConfig'
import MonthlyFixedExpensesTracker from './MonthlyFixedExpensesTracker.vue'

// Props
const props = defineProps({
  agencies: {
    type: Array,
    default: () => []
  }
})

// State
const activeTab = ref('monthly')
const loading = ref(false)
const loadingHistory = ref(false)
const saving = ref(false)
const registering = ref(false)
const newFormValid = ref(false)
const showPendingDialog = ref(false)

const fixedExpenses = ref([])
const pendingExpenses = ref([])
const fixedExpenseHistory = ref([])
const totalEstimado = ref(0)

const filters = ref({
  agency_id: null,
  categoria: null
})

// Filtros para historial de pagos
const historyFilters = ref({
  fecha_desde: null,
  fecha_hasta: null,
  date_range: null,
  agency_id: null,
  categoria: null
})

// Opciones de rango de fecha
const dateRangeOptions = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'this_week', label: 'Semana actual' },
  { value: 'last_week', label: 'Última semana' },
  { value: 'this_month', label: 'Mes actual' },
  { value: 'last_month', label: 'Último mes' },
  { value: 'custom', label: 'Personalizado' }
]

const newExpense = ref({
  agency_id: null,
  nombre: '',
  categoria: null,
  monto_estimado: 0,
  dia_vencimiento: 'quincena',
  dia_especifico: null,
  fecha_fin_contrato: null,
  notas: '',
  usa_fondo_grupo: false  // Default: empresa cubre (no afecta fondo del grupo)
})

// Límite máximo de archivo: 5MB (el backend acepta hasta 10MB pero dejamos margen)
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

const registerDialog = ref({
  show: false,
  expense: null,
  monto_real: null,
  comprobante_numero: '',
  observaciones: '',
  documento: null,
  documentoPreview: null,
  documentoBase64: null,
  documentoError: null,      // Mensaje de error si archivo es muy grande
  documentoSize: null        // Tamaño del archivo para mostrar al usuario
})

const editDialog = ref({
  show: false,
  id: null,
  data: {}
})

// Diálogo de detalle para historial de gastos
const historyDetailDialog = ref({
  show: false,
  expense: null
})

const snackbar = ref({
  show: false,
  message: '',
  color: 'success'
})

const categoryOptions = [
  { value: 'alquiler', text: 'Alquiler' },
  { value: 'servicios_basicos', text: 'Servicios Básicos (Luz/Agua)' },
  { value: 'mantenimiento', text: 'Mantenimiento' },
  { value: 'otros', text: 'Otros' }
]

const diaVencimientoOptions = [
  { value: 'inicio_mes', text: 'Inicio de mes (día 1-5)' },
  { value: 'quincena', text: 'Quincena (día 15)' },
  { value: 'fin_mes', text: 'Fin de mes (último día)' },
  { value: 'dia_especifico', text: 'Día específico...' }
]

// Methods
const formatNumber = (num) => {
  return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0)
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  // Verificar que sea una fecha válida
  let dateObj
  // Si es solo fecha (YYYY-MM-DD), agregar hora local para evitar desfase UTC
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // Fecha sin hora: parsear como fecha local agregando T12:00:00
    dateObj = new Date(date + 'T12:00:00')
  } else {
    dateObj = new Date(date)
  }
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatDateLong = (date) => {
  if (!date) return 'N/A'
  let dateObj
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    dateObj = new Date(date + 'T12:00:00')
  } else {
    dateObj = new Date(date)
  }
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatDateTime = (date) => {
  if (!date) return 'N/A'
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return 'Fecha inválida'
  return dateObj.toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getCategoryColor = (categoria) => {
  const colors = {
    alquiler: 'purple',
    servicios_basicos: 'orange',
    mantenimiento: 'blue',
    otros: 'grey'
  }
  return colors[categoria] || 'grey'
}

const getCategoryColorDark = (categoria) => {
  const colors = {
    alquiler: '#6A1B9A',       // purple-darken-2
    servicios_basicos: '#E65100', // orange-darken-4
    mantenimiento: '#1565C0',  // blue-darken-3
    otros: '#424242'           // grey-darken-3
  }
  return colors[categoria] || '#424242'
}

const getCategoryIcon = (categoria) => {
  const icons = {
    alquiler: 'mdi-home',
    servicios_basicos: 'mdi-flash',
    mantenimiento: 'mdi-wrench',
    otros: 'mdi-dots-horizontal'
  }
  return icons[categoria] || 'mdi-tag'
}

const getCategoryText = (categoria) => {
  const texts = {
    alquiler: 'Alquiler',
    servicios_basicos: 'Servicios Básicos',
    mantenimiento: 'Mantenimiento',
    otros: 'Otros'
  }
  return texts[categoria] || categoria
}

const formatDiaVencimiento = (tipo, diaEspecifico) => {
  const textos = {
    inicio_mes: 'Inicio de mes (día 1-5)',
    quincena: 'Día 15 de cada mes',
    fin_mes: 'Último día de cada mes',
    dia_especifico: diaEspecifico ? `Día ${diaEspecifico} de cada mes` : 'Día específico'
  }
  // Retrocompatibilidad: si es un número, mostrar como día específico
  if (typeof tipo === 'number') {
    return `Día ${tipo} de cada mes`
  }
  return textos[tipo] || `Día ${tipo} de cada mes`
}

const isImageFile = (file) => {
  if (!file) return false
  const fileName = file.name || ''
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName)
}

// Helper para construir Data URI - maneja ambos casos: base64 puro o ya con prefijo
const getDocumentDataUri = (expense) => {
  const doc = expense.documento_evidencia
  if (!doc) return ''
  // Si ya tiene el prefijo data:, retornar tal cual
  if (doc.startsWith('data:')) return doc
  // Si no, construir el Data URI
  const tipo = expense.documento_evidencia_tipo || 'application/octet-stream'
  return `data:${tipo};base64,${doc}`
}

// Helper para obtener URL completa de archivos (sistema multer)
const getFullUrl = (path) => {
  if (!path) return ''
  // Si ya es una URL completa, devolverla
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  return `${getApiOriginUrl()}${path}`
}

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

const handleDocumentoChange = async (file) => {
  // Resetear estados
  registerDialog.value.documentoPreview = null
  registerDialog.value.documentoBase64 = null
  registerDialog.value.documentoError = null
  registerDialog.value.documentoSize = null
  
  if (!file) {
    return
  }
  
  // Guardar tamaño para mostrar al usuario
  registerDialog.value.documentoSize = formatFileSize(file.size)
  
  // 🚫 Validar tamaño del archivo ANTES de procesarlo
  if (file.size > MAX_FILE_SIZE_BYTES) {
    registerDialog.value.documentoError = `El archivo es demasiado grande (${formatFileSize(file.size)}). El tamaño máximo permitido es ${MAX_FILE_SIZE_MB}MB.`
    registerDialog.value.documento = null // Limpiar el archivo seleccionado
    console.warn(`⚠️ Archivo rechazado: ${file.name} (${formatFileSize(file.size)}) excede el límite de ${MAX_FILE_SIZE_MB}MB`)
    return
  }
  
  // Crear preview para imágenes
  if (isImageFile(file)) {
    const reader = new FileReader()
    reader.onload = (e) => {
      registerDialog.value.documentoPreview = e.target.result
    }
    reader.readAsDataURL(file)
  }
  
  // Convertir a base64 para enviar al servidor
  const base64Reader = new FileReader()
  base64Reader.onload = (e) => {
    // Extraer solo la parte base64 sin el prefijo data:...
    const base64 = e.target.result.split(',')[1]
    registerDialog.value.documentoBase64 = base64
  }
  base64Reader.readAsDataURL(file)
}

const isExpensePending = (expense) => {
  if (!expense.ultimo_registro) return true
  const inicioMes = new Date()
  inicioMes.setDate(1)
  inicioMes.setHours(0, 0, 0, 0)
  return new Date(expense.ultimo_registro) < inicioMes
}

const loadFixedExpenses = async () => {
  loading.value = true
  const params = {
    agency_id: filters.value.agency_id || undefined,
    categoria: filters.value.categoria || undefined
  }
  console.log('📥 [FieldFixedExpenses] Cargando gastos fijos con filtros:', params)
  try {
    const response = await ApiClient.get('/field-supervisor/fixed-expenses', { params })
    console.log('📥 [FieldFixedExpenses] Response:', response.data)
    // La respuesta ya viene desencapsulada por el interceptor del ApiClient
    // response.data contiene directamente {fixed_expenses: [...], total_estimado: ...}
    const data = response.data
    if (data?.fixed_expenses !== undefined) {
      fixedExpenses.value = data.fixed_expenses || []
      totalEstimado.value = data.total_estimado || 0
      console.log('✅ [FieldFixedExpenses] Gastos cargados:', fixedExpenses.value.length, 'total:', totalEstimado.value)
    } else {
      console.warn('⚠️ [FieldFixedExpenses] Formato de respuesta inesperado:', data)
    }
  } catch (error) {
    console.error('❌ [FieldFixedExpenses] Error cargando gastos fijos:', error)
  } finally {
    loading.value = false
  }
}

const loadPendingExpenses = async () => {
  try {
    const response = await ApiClient.get('/field-supervisor/fixed-expenses/pending')
    // Respuesta desencapsulada por ApiClient
    const data = response.data
    if (data?.pending !== undefined) {
      pendingExpenses.value = data.pending || []
    }
  } catch (error) {
    console.error('Error cargando pendientes:', error)
  }
}

// Función para aplicar rango de fecha en historial
const applyHistoryDateRange = (range) => {
  const today = new Date()
  const formatDate = (d) => d.toISOString().split('T')[0]
  
  historyFilters.value.date_range = range
  
  switch (range) {
    case 'today':
      historyFilters.value.fecha_desde = formatDate(today)
      historyFilters.value.fecha_hasta = formatDate(today)
      break
    case 'yesterday':
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      historyFilters.value.fecha_desde = formatDate(yesterday)
      historyFilters.value.fecha_hasta = formatDate(yesterday)
      break
    case 'this_week':
      const startOfWeek = new Date(today)
      startOfWeek.setDate(today.getDate() - today.getDay())
      historyFilters.value.fecha_desde = formatDate(startOfWeek)
      historyFilters.value.fecha_hasta = formatDate(today)
      break
    case 'last_week':
      const lastWeekEnd = new Date(today)
      lastWeekEnd.setDate(today.getDate() - today.getDay() - 1)
      const lastWeekStart = new Date(lastWeekEnd)
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6)
      historyFilters.value.fecha_desde = formatDate(lastWeekStart)
      historyFilters.value.fecha_hasta = formatDate(lastWeekEnd)
      break
    case 'this_month':
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      historyFilters.value.fecha_desde = formatDate(startOfMonth)
      historyFilters.value.fecha_hasta = formatDate(today)
      break
    case 'last_month':
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      historyFilters.value.fecha_desde = formatDate(lastMonthStart)
      historyFilters.value.fecha_hasta = formatDate(lastMonthEnd)
      break
    case 'custom':
      // No hacer nada, el usuario define las fechas
      break
    default:
      historyFilters.value.fecha_desde = null
      historyFilters.value.fecha_hasta = null
  }
  
  if (range !== 'custom') {
    loadExpenseHistory()
  }
}

const loadExpenseHistory = async () => {
  loadingHistory.value = true
  try {
    const params = { limit: 50, tipo_gasto: 'fijo' }
    if (historyFilters.value.fecha_desde) params.fecha_desde = historyFilters.value.fecha_desde
    if (historyFilters.value.fecha_hasta) params.fecha_hasta = historyFilters.value.fecha_hasta
    if (historyFilters.value.agency_id) params.agency_id = historyFilters.value.agency_id
    if (historyFilters.value.categoria) params.categoria = historyFilters.value.categoria
    
    const response = await ApiClient.get('/field-supervisor/expenses', { params })
    // Respuesta desencapsulada por ApiClient
    const data = response.data
    if (data?.expenses !== undefined) {
      fixedExpenseHistory.value = data.expenses || []
    }
  } catch (error) {
    console.error('Error cargando historial:', error)
  } finally {
    loadingHistory.value = false
  }
}

const createFixedExpense = async () => {
  saving.value = true
  try {
    const response = await ApiClient.post('/field-supervisor/fixed-expenses', newExpense.value)
    // Si llegamos aquí sin error, la operación fue exitosa (ApiClient lanza error si success=false)
    snackbar.value = { show: true, message: '✅ Gasto fijo configurado', color: 'success' }
    resetNewForm()
    loadFixedExpenses()
    activeTab.value = 'list'
  } catch (error) {
    snackbar.value = {
      show: true,
      message: `❌ ${error.response?.data?.error || 'Error al guardar'}`,
      color: 'error'
    }
  } finally {
    saving.value = false
  }
}

const openEditDialog = (expense) => {
  editDialog.value = {
    show: true,
    id: expense.id,
    data: { ...expense }
  }
}

const updateFixedExpense = async () => {
  saving.value = true
  try {
    await ApiClient.put(`/field-supervisor/fixed-expenses/${editDialog.value.id}`, editDialog.value.data)
    // Si llegamos aquí sin error, la operación fue exitosa
    snackbar.value = { show: true, message: '✅ Gasto fijo actualizado', color: 'success' }
    editDialog.value.show = false
    loadFixedExpenses()
  } catch (error) {
    snackbar.value = {
      show: true,
      message: `❌ ${error.response?.data?.error || 'Error al actualizar'}`,
      color: 'error'
    }
  } finally {
    saving.value = false
  }
}

const confirmDelete = async (expense) => {
  if (confirm(`¿Desactivar gasto fijo "${expense.nombre}"?`)) {
    try {
      await ApiClient.delete(`/field-supervisor/fixed-expenses/${expense.id}`)
      snackbar.value = { show: true, message: '✅ Gasto fijo desactivado', color: 'success' }
      loadFixedExpenses()
    } catch (error) {
      snackbar.value = {
        show: true,
        message: `❌ ${error.response?.data?.error || 'Error al desactivar'}`,
        color: 'error'
      }
    }
  }
}

const openRegisterDialog = (expense) => {
  registerDialog.value = {
    show: true,
    expense,
    monto_real: null,
    comprobante_numero: '',
    observaciones: '',
    documento: null,
    documentoPreview: null,
    documentoBase64: null,
    documentoError: null,
    documentoSize: null
  }
}

const openHistoryDetailDialog = async (expense) => {
  // Mostrar diálogo con datos básicos mientras carga el detalle
  historyDetailDialog.value = {
    show: true,
    expense: { ...expense, loading: true }
  }
  
  try {
    // Cargar detalle completo con documento
    const response = await ApiClient.get(`/field-supervisor/expenses/${expense.id}`)
    const expenseData = response?.data || response
    historyDetailDialog.value.expense = { ...expenseData, loading: false }
  } catch (error) {
    console.error('Error cargando detalle:', error)
    // Mantener datos básicos si falla
    historyDetailDialog.value.expense = { ...expense, loading: false }
  }
}

const registerPayment = async () => {
  registering.value = true
  try {
    const payload = {
      monto_real: registerDialog.value.monto_real,
      comprobante_numero: registerDialog.value.comprobante_numero,
      observaciones: registerDialog.value.observaciones
    }
    
    // Agregar documento si existe (nombres alineados con backend)
    if (registerDialog.value.documentoBase64 && registerDialog.value.documento) {
      payload.documento_evidencia = registerDialog.value.documentoBase64
      payload.documento_evidencia_nombre = registerDialog.value.documento.name
      payload.documento_evidencia_tipo = registerDialog.value.documento.type
    }
    
    await ApiClient.post(
      `/field-supervisor/fixed-expenses/${registerDialog.value.expense.id}/register`,
      payload
    )
    // Si llegamos aquí sin error, la operación fue exitosa
    snackbar.value = { show: true, message: '✅ Pago registrado exitosamente', color: 'success' }
    registerDialog.value.show = false
    loadFixedExpenses()
    loadPendingExpenses()
    loadExpenseHistory()
  } catch (error) {
    // Detectar errores específicos del servidor
    let errorMessage = 'Error al registrar pago'
    const serverError = error.response?.data?.error || error.message || ''
    
    // Manejar error de payload muy grande (fallback si validación frontend falla)
    if (error.response?.status === 413 || serverError.includes('too large') || serverError.includes('entity too large')) {
      errorMessage = `El archivo adjunto es demasiado grande. Máximo permitido: ${MAX_FILE_SIZE_MB}MB`
      registerDialog.value.documentoError = errorMessage
    } else {
      errorMessage = serverError || errorMessage
    }
    
    snackbar.value = {
      show: true,
      message: `❌ ${errorMessage}`,
      color: 'error'
    }
  } finally {
    registering.value = false
  }
}

const resetNewForm = () => {
  newExpense.value = {
    agency_id: null,
    nombre: '',
    categoria: null,
    monto_estimado: 0,
    dia_vencimiento: 'quincena',
    dia_especifico: null,
    fecha_fin_contrato: null,
    notas: '',
    usa_fondo_grupo: false
  }
}

// Handlers para eventos del componente MonthlyFixedExpensesTracker
const onMonthlyExpensePaid = (data) => {
  console.log('✅ Pago registrado desde seguimiento mensual:', data)
  // Recargar historial y pendientes
  loadExpenseHistory()
  loadPendingExpenses()
}

const onMonthlyExpenseSkipped = (data) => {
  console.log('ℹ️ Gasto omitido desde seguimiento mensual:', data)
}

const onMonthGenerated = (data) => {
  console.log('📅 Mes generado desde seguimiento mensual:', data)
}

// Lifecycle
onMounted(() => {
  loadFixedExpenses()
  loadPendingExpenses()
  loadExpenseHistory()
})
</script>

<style scoped>
.field-fixed-expenses-panel {
  height: 100%;
}

/* Estilo para items clickeables del historial */
.history-item {
  transition: all 0.2s ease;
}

.history-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
  transform: translateX(4px);
}

.cursor-pointer {
  cursor: pointer;
}

/* Estilos para el diálogo de detalle */
.expense-detail-card {
  overflow: hidden;
}

.expense-detail-header {
  color: white;
}

.info-card {
  transition: all 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Estilos para preview del documento */
.document-preview {
  max-height: 400px;
  overflow: auto;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px;
}

.document-preview img {
  width: 100%;
  height: auto;
  border-radius: 4px;
}
</style>
